import { Link, useNavigate } from 'react-router-dom'
import { CheckCircleIcon, GiftIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/solid'
import { useSession } from '@/context/SessionContext'
import { useProfile } from '@/context/ProfileContext'
import { computePersonalization } from '@/lib/personalization'
import { markOnboardingNextStepSeen } from '@/lib/queries/auth'
import { FREE_TIER_MESSAGE_LIMIT } from '@/lib/constants'
import { capture } from '@/lib/posthog'

const GOAL_MESSAGES = {
  deepen_faith:
    "I'm here to walk with you — let's build a deeper devotional life together, one conversation at a time.",
  understand_bible:
    "Let's open the Word together. I'll help you see the history, the meaning, and how it speaks to your life today.",
  defend_faith:
    "When someone challenges what you believe, you'll be ready. Let's sharpen your confidence together.",
  sermon_prep:
    "Your next message deserves your best preparation. I'm here to help you craft something that moves hearts.",
  answer_questions:
    "No question is too hard or too honest. Bring me whatever's on your mind and let's work through it.",
  explore_christianity:
    "Welcome — there's no pressure here. Let's explore the Christian faith at whatever pace feels right.",
}

export default function Welcome() {
  const { user } = useSession()
  const { profile } = useProfile()
  const navigate = useNavigate()

  const goalKey = profile?.primaryGoal
  const personalMessage = goalKey ? GOAL_MESSAGES[goalKey] : null

  // Onboarding Personalization v1 — the announced "next step" completion card.
  // Only shows on the first post-onboarding landing; suppressed once the user
  // has dismissed/acted on it (stored on profile.onboarding_next_step_seen_at).
  const personalization = computePersonalization(profile)
  const showCompletionCard =
    personalization.isEnabled &&
    personalization.completionCard &&
    !profile?.onboardingNextStepSeenAt

  async function handleCompletionCardClick() {
    const card = personalization.completionCard
    if (!card) return
    capture('welcome_card_clicked', { destination: card.destinationTab })
    if (user?.id) {
      try {
        await markOnboardingNextStepSeen(user.id)
      } catch (err) {
        // Non-fatal — user can still proceed.
        console.warn('Failed to mark completion card seen:', err)
      }
    }
    const encodedPrompt = encodeURIComponent(card.starterPrompt || '')
    navigate(`/chat?tab=${card.destinationTab}&prompt=${encodedPrompt}`)
  }

  async function handleSecondaryCta() {
    if (showCompletionCard) capture('welcome_card_skipped')
    if (showCompletionCard && user?.id) {
      try {
        await markOnboardingNextStepSeen(user.id)
      } catch {
        // Non-fatal
      }
    }
    // When the personalization completion card IS shown, the secondary
    // button is the "Or skip to home" exit ramp → /home. When the card
    // is NOT shown (user opted out of personalization, or no role/goal),
    // this becomes the primary "Start Your First Conversation" CTA — and
    // it must actually go to /chat so the button text doesn't lie.
    // The chat empty state's starter-prompt chips will guide them from
    // there.
    navigate(showCompletionCard ? '/home' : '/chat')
  }

  return (
    <div className="max-w-md w-full text-center">
      <div className="animate-fade-in-up">
        <img
          src="/FinalLogo.png"
          alt="Illuminate Faith"
          className="h-20 w-auto mx-auto mb-6 rounded-lg"
          style={{ clipPath: 'inset(2% round 4px)' }}
        />
      </div>

      <h1 className="animate-fade-in-up delay-200 font-heading text-[var(--text-primary)] text-3xl md:text-4xl font-bold">
        You&rsquo;re All Set!
      </h1>

      {personalMessage ? (
        <p className="animate-fade-in-up delay-300 text-[var(--text-muted)] text-lg max-w-sm mx-auto leading-relaxed mt-4 italic">
          &ldquo;{personalMessage}&rdquo;
        </p>
      ) : (
        <p className="animate-fade-in-up delay-300 text-[var(--text-muted)] text-lg max-w-sm mx-auto leading-relaxed mt-4">
          Everything is set up and ready for you. Ask about any verse, wrestle with a tough
          question, or just start a conversation about faith.
        </p>
      )}

      {/* Free tier callout */}
      <div className="animate-fade-in-up delay-400 mt-6 mx-auto max-w-sm bg-[var(--bg-surface)] border border-[var(--glass-border)] rounded-xl px-5 py-4 text-left">
        <div className="flex items-center gap-2 mb-2">
          <GiftIcon className="w-4 h-4 text-gold shrink-0" aria-hidden="true" />
          <span className="text-gold text-sm font-bold">
            {FREE_TIER_MESSAGE_LIMIT} Free Messages Every Month
          </span>
        </div>
        <p className="text-[var(--text-muted)] text-sm leading-relaxed">
          You get {FREE_TIER_MESSAGE_LIMIT} free messages every month with Grace, your personal
          faith companion. No credit card needed. When you're ready for more, upgrade to unlock all
          four AI companions and unlimited conversations.
        </p>
      </div>

      {/* Announced personalization — the ONE surface that explicitly
          references the user's role + goal. */}
      {showCompletionCard && (
        <div className="animate-fade-in-up delay-500 mt-6 mx-auto max-w-sm">
          <div className="rounded-2xl border border-gold/40 bg-gold/5 p-5 text-left">
            <div className="flex items-start gap-3 mb-4">
              <ChatBubbleLeftRightIcon
                className="w-5 h-5 text-gold shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <p className="text-[var(--text-primary)] text-sm leading-relaxed">
                {personalization.completionCard.announceCopy}
              </p>
            </div>
            <button
              onClick={handleCompletionCardClick}
              className="w-full bg-gold hover:bg-gold-light text-midnight font-bold py-3 rounded-lg transition-all duration-300 hover:scale-[1.02]"
            >
              {personalization.completionCard.ctaLabel}
            </button>
          </div>
        </div>
      )}

      <button
        onClick={handleSecondaryCta}
        className={`animate-fade-in-up inline-block bg-gold hover:bg-gold-light text-midnight font-bold px-8 py-4 rounded-lg text-lg transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-gold/20 mt-6 ${showCompletionCard ? 'delay-600 !bg-transparent !text-[var(--text-muted)] hover:!bg-[var(--bg-surface)] hover:!text-[var(--text-primary)] !shadow-none border border-[var(--glass-border)]' : 'delay-500'}`}
      >
        {showCompletionCard ? 'Or skip to home →' : 'Start Your First Conversation →'}
      </button>

      <div className={`animate-fade-in-up mt-4 ${showCompletionCard ? 'delay-700' : 'delay-600'}`}>
        <Link to="/" className="text-[var(--text-muted)] hover:text-gold text-sm transition-colors">
          or go to homepage
        </Link>
      </div>
    </div>
  )
}
